import React from 'react';
import { orderBy } from 'lodash';

import { Box, Text } from 'core/components';
import { SelectOption, SelectSubOption } from 'core/components/types';
import { Field, Select } from 'core/form';
import DashboardSelector from 'app/views/core/dashboards/DashboardSelector';
import { AGGREGATE_VIEWS, QUICK_VIEW_ROUTE } from 'app/views/core';
import { LandingSubOptionsSource } from 'app/stores/rbac/$rbac';

interface LandingBaseOption {
  label: string;
  value: string;
  subOptionsSource?: string;
}

/**
 * Returns `{ disabled, disabledTooltip }` for a landing sub-option given the resource's label IDs.
 * Return `{ disabled: false }` (or omit the field) when the option should be enabled.
 */
export type DisabledPropsGetter = (labelIds: number[]) => { disabled?: boolean; disabledTooltip?: string };

export interface LandingResolvedOption extends LandingBaseOption {
  subOptions?: SelectSubOption[];
}

interface LandingPageTypeOptionsParams {
  currentType: string;
  boardOptions: SelectOption[];
  savedViewOptions: SelectOption[];
  hasMarketIntel?: boolean;
  hasAiAdvisor?: boolean;
  menuWidth?: number | 'auto';
  /** Form field name for the sub-select value. Defaults to 'defaultLandingPageValue'. */
  valueFieldName?: string;
  /** Extra props spread into the DashboardSelector sub-select (e.g. { disabledIds, disabledTooltip }). */
  dashboardSelectorParams?: Record<string, unknown>;
}

interface LandingPageSubOptionStores {
  $boards: {
    assignableBoards: Array<{ id: number | string; name: string; labelIds?: number[] }>;
  };
  $dashboards: {
    collection: {
      get: () => Array<{ id: number | string; get: (key: string) => unknown; labelIds?: number[] }>;
    };
  };
  $savedViews: {
    collection: {
      get: () => Array<{ id: number | string; get: (key: string) => unknown; labelIds?: number[] }>;
    };
  };
}

/**
 * Returns quick-view select options sorted by label, derived from AGGREGATE_VIEWS.
 */
export function generateQuickViewOptions(): SelectOption[] {
  const options = AGGREGATE_VIEWS.map((view) => ({
    value: view.path.replace(`${QUICK_VIEW_ROUTE}/`, ''),
    label: view.name
  }));
  return orderBy(options, 'label');
}

/**
 * Builds the landing-page radio-group options array (with inline JSX sub-selects)
 * used by UserInfo and General forms.
 *
 * @param params.currentType     - Current value of the `defaultLandingPageType` field.
 * @param params.boardOptions    - Board select options (caller decides self vs assignable boards).
 * @param params.savedViewOptions - Saved-view select options.
 * @param params.hasMarketIntel  - When true, appends the "Market Intelligence" option.
 * @param params.hasAiAdvisor    - When true, includes the "Kentik AI Advisor" option.
 * @param params.menuWidth       - Optional width forwarded to sub-select menus (General uses 235).
 * @param params.valueFieldName  - Form field name for sub-select values. Defaults to 'defaultLandingPageValue'.
 */
export function buildLandingPageTypeOptions({
  currentType,
  boardOptions,
  savedViewOptions,
  hasMarketIntel = false,
  hasAiAdvisor = false,
  menuWidth,
  valueFieldName = 'defaultLandingPageValue',
  dashboardSelectorParams = {}
}: LandingPageTypeOptionsParams) {
  const options = [
    {
      label: (
        <Box>
          <Text as="div">Observation Decks</Text>
          {currentType === 'observationDeck' && (
            <Field name={valueFieldName} options={orderBy(boardOptions, 'label')} mt={1}>
              <Select showFilter menuWidth={menuWidth} />
            </Field>
          )}
        </Box>
      ),
      value: 'observationDeck'
    },
    { label: 'NMS Dashboard', value: 'nmsDashboard' },
    { label: 'Network Explorer', value: 'networkExplorer' },
    { label: 'Library', value: 'library' },
    { label: 'Synthetics', value: 'synthetics' },
    { label: 'Data Explorer', value: 'explorer' },
    { label: 'Alerting', value: 'alerting' },
    { label: 'Insights', value: 'insights' },
    ...(hasAiAdvisor ? [{ label: 'Kentik AI Advisor', value: 'aiAdvisor' }] : []),
    {
      label: (
        <Box>
          <Text as="div">Dashboards</Text>
          {currentType === 'dashboard' && (
            <DashboardSelector mt={1} name={valueFieldName} menuWidth={menuWidth} {...dashboardSelectorParams} />
          )}
        </Box>
      ),
      value: 'dashboard'
    },
    {
      label: (
        <Box>
          <Text as="div">Saved Views</Text>
          {currentType === 'saved-view' && (
            <Field name={valueFieldName} options={orderBy(savedViewOptions, 'label')} mt={1}>
              <Select showFilter menuWidth={menuWidth} />
            </Field>
          )}
        </Box>
      ),
      value: 'saved-view'
    },
    {
      label: (
        <Box>
          <Text as="div">Quick Views</Text>
          {currentType === 'quick-view' && (
            <Field name={valueFieldName} options={generateQuickViewOptions()} mt={1}>
              <Select showFilter menuWidth={menuWidth} />
            </Field>
          )}
        </Box>
      ),
      value: 'quick-view'
    }
  ];

  if (hasMarketIntel) {
    options.push({ label: 'Market Intelligence', value: 'market-intel' });
  }

  return options;
}

/**
 * Builds a map of LandingSubOptionsSource keys to resolver thunks.
 * Each resolver produces the flat `{ label, value }` sub-option list for its landing type,
 * using compound values (e.g. "dashboard:42") that bulkUpdateUsers splits into
 * defaultLandingPageType + defaultLandingPageValue.
 *
 * Used by the Users admin table bulk-action landing-page dropdown.
 */
export function buildLandingPageSubOptionsResolvers({
  stores,
  disabledPropsGetters
}: {
  stores: LandingPageSubOptionStores;
  disabledPropsGetters?: Partial<Record<string, DisabledPropsGetter>>;
}): Record<string, () => SelectSubOption[]> {
  const { $boards, $dashboards, $savedViews } = stores;

  // Build Select sub-options for Boards
  const boardOptions = $boards.assignableBoards.map((model) => {
    const label = model.name as string;
    const value = `observationDeck:${model.id}` as string;
    const disabledPropsGetter = disabledPropsGetters?.[LandingSubOptionsSource.BOARDS];
    const labelIds = model.labelIds ?? ([] as number[]);

    // Set disabled props or fallback to undefined based on the disabledPropsGetter function param
    const { disabled, disabledTooltip } = disabledPropsGetter ? disabledPropsGetter(labelIds) : {};
    return { label, value, disabled, disabledTooltip };
  });

  // Build Select sub-options for Dashboards
  const dashOptions = $dashboards.collection.get().map((model) => {
    const label = model.get('dash_title') as string;
    const value = `dashboard:${model.get('id')}` as string;
    const disabledPropsGetter = disabledPropsGetters?.[LandingSubOptionsSource.DASHBOARDS];
    const labelIds = model.labelIds ?? ([] as number[]);

    // Set disabled props or fallback to undefined based on the disabledPropsGetter function param
    const { disabled, disabledTooltip } = disabledPropsGetter ? disabledPropsGetter(labelIds) : {};

    return { label, value, disabled, disabledTooltip };
  });

  // Build Select sub-options for Saved Views
  const savedViewOptions = $savedViews.collection.get().map((model) => {
    const label = model.get('view_name') as string;
    const value = `saved-view:${model.id}` as string;
    const disabledPropsGetter = disabledPropsGetters?.[LandingSubOptionsSource.SAVED_VIEWS];
    const labelIds = model.labelIds ?? ([] as number[]);

    // Set disabled props or fallback to undefined based on the disabledPropsGetter function param
    const { disabled, disabledTooltip } = disabledPropsGetter ? disabledPropsGetter(labelIds) : {};

    return { label, value, disabled, disabledTooltip };
  });

  return {
    [LandingSubOptionsSource.BOARDS]: () => orderBy(boardOptions, 'label'),
    [LandingSubOptionsSource.DASHBOARDS]: () => orderBy(dashOptions, 'label'),
    [LandingSubOptionsSource.SAVED_VIEWS]: () => orderBy(savedViewOptions, 'label'),
    [LandingSubOptionsSource.QUICK_VIEWS]: () => orderBy(generateQuickViewOptions(), 'label')
  };
}

/**
 * Resolves `subOptionsSource` entries in a base options list into concrete `subOptions` arrays.
 * Returns a new array; options without a matching resolver are returned unchanged.
 *
 * Used by the Users admin table to build the landing-page bulk-action dropdown.
 */
export function resolveLandingPageOptions({
  baseOptions,
  resolvers
}: {
  baseOptions: LandingBaseOption[];
  resolvers: Record<string, () => SelectSubOption[]>;
}): LandingResolvedOption[] {
  return baseOptions.map((option) => {
    if (option.subOptionsSource) {
      const resolver = resolvers[option.subOptionsSource];
      if (resolver) {
        const subOptions: SelectSubOption[] = resolver();
        if (subOptions.length > 0) {
          return { ...option, subOptions };
        }
      }
    }
    return option;
  });
}
